import type { Quest } from "./types";
import { STAT_ICONS } from "./types";
import { state, valuesFor } from "./state";
import { LEVELS } from "./levels";
import { heroesFor } from "./payoff";
import { boardEl, benchEl } from "../dom";

/** "25g + 6×⚔" — a contract's terms, listing only the stats it pays for. */
export function termsLine(q: Quest): string {
  const parts = [`${q.base}g`];
  q.weights.forEach((w, k) => {
    if (w > 0) parts.push(`${w}×${STAT_ICONS[k]}`);
  });
  return parts.join(" + ");
}

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K, cls: string, text?: string,
): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

/* ---------- Build (per level) ---------- */
export function buildBoard(): void {
  const L = state.level();
  const heroes = state.heroes();

  boardEl.innerHTML = "";
  L.quests.forEach((q, j) => {
    const card = el("button", "quest");
    card.type = "button";
    card.dataset.q = String(j);
    card.appendChild(el("span", "q-emoji", q.emoji));
    card.appendChild(el("span", "q-name", q.name));
    card.appendChild(el("span", "q-terms", termsLine(q)));
    card.appendChild(el("span", "q-slot empty", "— open —"));
    card.appendChild(el("span", "q-offer"));
    boardEl.appendChild(card);
  });

  benchEl.innerHTML = "";
  heroes.forEach((h, i) => {
    const card = el("button", "hero");
    card.type = "button";
    card.dataset.h = String(i);
    card.appendChild(el("span", "h-emoji", h.emoji));
    const body = el("span", "h-body");
    body.appendChild(el("span", "h-name", h.name));
    const stats = el("span", "h-stats");
    h.stats.forEach((s, k) => stats.appendChild(el("span", `st st${k}`, `${STAT_ICONS[k]} ${s}`)));
    body.appendChild(stats);
    card.appendChild(body);
    card.appendChild(el("span", "h-where"));
    benchEl.appendChild(card);
  });
}

/* ---------- Render (every state change) ---------- */
export function renderBoard(): void {
  const v = state.values();
  const heroes = state.heroes();
  const sel = state.selected;

  benchEl.querySelectorAll<HTMLElement>(".hero").forEach((card) => {
    const i = Number(card.dataset.h);
    const j = state.assign[i];
    card.classList.toggle("selected", i === sel);
    card.classList.toggle("assigned", j >= 0);
    const where = card.querySelector(".h-where")!;
    where.textContent = j >= 0 ? `on ${state.level().quests[j].emoji}` : "";
  });

  boardEl.querySelectorAll<HTMLElement>(".quest").forEach((card) => {
    const j = Number(card.dataset.q);
    const occupant = state.assign.indexOf(j);
    const slot = card.querySelector(".q-slot")!;
    if (occupant >= 0) {
      const h = heroes[occupant];
      slot.textContent = `${h.emoji} ${h.name.split(" ")[0]} · ${v[occupant][j]}g`;
      slot.className = "q-slot filled";
    } else {
      slot.textContent = "— open —";
      slot.className = "q-slot empty";
    }

    const offer = card.querySelector(".q-offer")!;
    if (sel >= 0) {
      offer.textContent = sel === occupant ? "tap to recall" : `pays ${v[sel][j]}g`;
    } else {
      offer.textContent = "";
    }
    card.classList.toggle("offering", sel >= 0 && sel !== occupant);
    card.classList.toggle("current", sel >= 0 && sel === occupant);
    card.classList.toggle("filled", occupant >= 0);
  });
}

/** A short-lived "+74g" float over a quest card when a hero is posted. */
export function flashGain(questIdx: number, amount: number): void {
  const card = boardEl.querySelector<HTMLElement>(`.quest[data-q="${questIdx}"]`);
  if (!card) return;
  const f = el("span", "floaty", `+${amount}g`);
  card.appendChild(f);
  window.setTimeout(() => f.remove(), 1000);
}

/* ---------- Mini ledger (the reveal's matrix view) ---------- */
/**
 * Render a level's payoff matrix with the chosen cells highlighted — the
 * "you've been filling a matrix all along" moment. `assign[heroIdx] =
 * questIdx`, -1/missing = not chosen (supports the reveal's row-by-row
 * animation). `kind` colors the picks ("you" silver, "opt" gold).
 */
export function buildMiniLedger(
  holder: HTMLElement, levelIdx: number, assign: number[], kind: "you" | "opt",
): void {
  const L = LEVELS[levelIdx];
  const v = valuesFor(levelIdx);
  const heroes = heroesFor(L);
  const n = L.heroCount;

  const grid = el("div", `mini mini-${kind}`);
  grid.style.gridTemplateColumns = `auto repeat(${n}, 1fr)`;
  grid.appendChild(el("span", "m-corner", ""));
  L.quests.forEach((q) => grid.appendChild(el("span", "m-qh", q.emoji)));
  heroes.forEach((h, i) => {
    grid.appendChild(el("span", "m-hh", h.emoji));
    L.quests.forEach((_, j) => {
      const cell = el("span", "m-cell", String(v[i][j]));
      if (assign[i] === j) cell.classList.add("pick");
      grid.appendChild(cell);
    });
  });

  holder.innerHTML = "";
  holder.appendChild(grid);
}
